/**
 * Where & what — the environment x activity cross-tab.
 *
 * Built for the recurring customer question, of which "what activities do you
 * capture outside apartment complex buildings?" was the first: pick a setting,
 * get the activities that happen in it, ranked by how much they CONCENTRATE
 * there rather than by how common they are.
 *
 * Three decisions the numbers here depend on, all of them load-bearing:
 *
 * 1. Ranked by lift, not by share. "Using a phone" is the biggest activity in
 *    almost every environment (28% of all tagged footage), so a share ranking
 *    returns the same answer everywhere and answers nothing. Lift is the share
 *    inside the environment over the share across the whole selection, so 1.0x
 *    means "no more likely here than anywhere" and only a number well above it
 *    is a fact about this setting. Same test that convicted an empty
 *    environment label on industry concentration; it transfers directly.
 *
 * 2. Read off the RAW hits, not taskOf. taskOf collapses each video to one
 *    majority activity because a manifest needs a single column; a video shot
 *    outside an apartment block can legitimately show keys AND a door AND a
 *    citation terminal, and collapsing it throws away two thirds of the little
 *    evidence there is. Rows therefore overlap: the percentages do not sum to
 *    100 and the hours double-count across activities.
 *
 * 3. The denominator is always visible. Activity tags come from descriptionsv1,
 *    which only holds a description where the wearer-hands detector fired --
 *    about 4% of the corpus, and as little as 2% in some environments. A
 *    ranking off 108 of 4,236 videos is worth having; the same ranking presented
 *    as though it described 4,236 misleads a customer. Hence the coverage bar
 *    beside every title, amber under 10% -- shown rather than explained, which
 *    is the rule the rest of this panel follows too: the lift bar replaced a
 *    paragraph about what lift means.
 *
 * Costs nothing to compute: both inputs are already in AnalysisPage state --
 * `envs` from the environment cache and `activityHits` from the warm sweep the
 * tab already runs. This panel adds no fetch of its own except the one it makes
 * when you open a cell to read the actual descriptions behind it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CorpusVideo,
  EnvLabel,
  SEARCH_HIT_LIMIT,
  SearchHit,
  TOP_ACTIVITIES,
  fmtHours,
  runDescriptionSearch,
} from './whShared';
import './DatasetPage.css';
import './EnvActivityPanel.css';

const UNCLASSIFIED = 'setting unknown';

/** Lift at or above this, on at least MIN_N videos, is called out. Two separate
 *  conditions on purpose: 4 videos out of 6 is a 40x lift and means nothing. */
const LIFT_STRONG = 1.6;
const MIN_N = 3;

/** Lift, in words. The multiplier is the honest number but it is not what
 *  someone reads an answer off, and naming it "lift" needed a legend to explain
 *  a legend. The exact value stays on hover for anyone who wants it. */
function liftWords(lift: number, weak: boolean): string {
  if (weak) return 'too few to say';
  if (lift >= LIFT_STRONG) return 'much more common here';
  if (lift >= 1.15) return 'more common here';
  if (lift >= 0.85) return 'about average';
  return 'less common here';
}

/** Below this share of an environment's videos carrying any tag, the ranking is
 *  labelled thin rather than presented flat. 10% is not a statistical threshold,
 *  it is the point where the honest sentence changes from "here is the mix" to
 *  "here is what the few tagged videos show". */
const THIN_COVERAGE = 0.1;

/** A hit at or above this similarity is a real match rather than a near-floor
 *  coincidence. vectordb's own floor is 0.45, which is generous: swept across
 *  the whole taxonomy, 'Using a handheld terminal' returns 290 hits and NOT ONE
 *  of them clears 0.55 -- every match is the words "handheld" or "device"
 *  appearing near something else. Counting those as videos is how a chip with no
 *  real footage behind it produced a lift row.
 *
 *  0.55, not the 0.60 this shipped with. Read off the descriptions themselves at
 *  the boundary, in apartment complex exterior:
 *
 *    0.578  "picking up a white bag from the ground near a dumpster"   real
 *    0.562  "holding a clear plastic bag ... moving it slightly"       real
 *    0.595  "pressing buttons on it, likely to operate a door"         real
 *    0.592  "manipulating an object, possibly a tool or device"        generic
 *
 *  Short concrete queries land lower than vague ones do, so 0.60 was silencing
 *  chips with real footage while the vague-query false positives sat just above
 *  it. MIN_STRONG = 2 is what separates those last two lines, not the similarity.
 *  Together they take this environment from 5 ranked activities to 13 over the
 *  whole environment (3 to 11 in the tab's default selection), and every chip
 *  known to have no footage behind it stays blocked -- handheld terminal best
 *  0.51, cones 0.49, ticketing 0.52, the last two despite 3.8x and 2.2x lift.
 *
 *  Deliberately NOT applied as a filter on the counts: the tallies stay at the
 *  full floor so coverage is unchanged and nothing already correct is lost. It
 *  gates only the RANKING -- a chip has to show MIN_STRONG genuine matches
 *  before it is allowed to claim "much more common here". */
const STRONG_SIM = 0.55;
const MIN_STRONG = 2;

type Dir = 'env' | 'act';

interface Cell {
  key: string;
  videos: number;
  seconds: number;
  /** share of this group's TAGGED videos */
  share: number;
  /** share over the whole selection, the baseline `share` is measured against */
  base: number;
  lift: number;
  /** videos matching this cell above STRONG_SIM — the ranking gate */
  strong: number;
}

interface Group {
  key: string;
  /** every video in the selection with this env (or this activity) */
  videos: number;
  seconds: number;
  /** the subset carrying at least one activity tag (or, for direction 'act',
   *  at least one environment — which is every video, hence 1.0) */
  tagged: number;
  taggedSeconds: number;
  cells: Cell[];
}

/** Lift as a diverging bar around a fixed 1.0x centre line. This exists so the
 *  panel does not have to explain lift in prose: right of the tick means "more
 *  here than elsewhere", left means less, and the two-row key at the bottom is
 *  the whole definition. Clipped at 3x — beyond that the exact multiple stops
 *  mattering and only the direction does. */
function LiftBar({ lift }: { lift: number }) {
  const MAX = 3;
  const v = Math.min(Math.max(lift, 0), MAX);
  const up = v >= 1;
  const w = up ? ((v - 1) / (MAX - 1)) * 50 : (1 - v) * 50;
  return (
    <span className="ea-bar" aria-hidden="true">
      <i
        className={up ? 'up' : 'dn'}
        style={{ left: up ? '50%' : `${50 - w}%`, width: `${Math.max(w, 0.8)}%` }}
      />
    </span>
  );
}

export default function EnvActivityPanel({
  videos, envs, activityHits, warming, hiddenEnvs, onClose,
}: {
  /** The selection, already filtered — this panel never re-filters. */
  videos: CorpusVideo[];
  envs: { [fn: string]: EnvLabel };
  /** Raw per-query hits from the warm sweep, keyed by query string. */
  activityHits: { [q: string]: SearchHit[] };
  /** Activity queries still in flight; every number below grows until it is 0. */
  warming: number;
  /** Environments the Selection filters are currently excluding. Named rather
   *  than silently missing: a matrix with rows removed looks complete. */
  hiddenEnvs: string[];
  onClose: () => void;
}) {
  const [dir, setDir] = useState<Dir>('env');
  const [pick, setPick] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // Rows that cannot be ranked are collapsed: they are the long tail, so they
  // pad the answer without adding to it. Collapsed, not greyed -- the counts in
  // them are real and worth reading, it is only the comparison that is withheld.
  const [showThin, setShowThin] = useState(false);

  // filename -> the activities it matched, from the raw hits. A Set because a
  // video reached by three phrasings of "Spraying" is one spraying video.
  const actsOf = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    TOP_ACTIVITIES.forEach((a) =>
      a.queries.forEach((q) =>
        (activityHits[q] || []).forEach((h) => {
          const per = m.get(h.filename) || new Map<string, number>();
          // Best similarity wins: three phrasings of one activity are one
          // video, and the strongest phrasing is what it actually shows.
          per.set(a.label, Math.max(per.get(a.label) ?? 0, h.similarity));
          m.set(h.filename, per);
        }),
      ),
    );
    return m;
  }, [activityHits]);

  // One pass over the selection builds both directions. Kept together so the
  // two views can never disagree about a count.
  const { groups } = useMemo(() => {
    // Baselines over the whole selection, the denominator of every lift.
    let taggedAll = 0;
    const actAll = new Map<string, number>();
    const envAll = new Map<string, number>();
    videos.forEach((v) => {
      const a = actsOf.get(v.filename);
      const e = envs[v.filename]?.environment || UNCLASSIFIED;
      envAll.set(e, (envAll.get(e) || 0) + 1);
      if (a && a.size) {
        taggedAll += 1;
        a.forEach((_sim, x) => actAll.set(x, (actAll.get(x) || 0) + 1));
      }
    });

    const g = new Map<string, Group & { cellMap: Map<string, Cell> }>();
    const touch = (key: string): Group & { cellMap: Map<string, Cell> } => {
      let row = g.get(key);
      if (!row) {
        row = {
          key, videos: 0, seconds: 0, tagged: 0, taggedSeconds: 0,
          cells: [], cellMap: new Map(),
        };
        g.set(key, row);
      }
      return row;
    };
    const bump = (row: { cellMap: Map<string, Cell> }, key: string, secs: number,
                  strong: boolean) => {
      const c = row.cellMap.get(key)
        || { key, videos: 0, seconds: 0, share: 0, base: 0, lift: 0, strong: 0 };
      c.videos += 1;
      c.seconds += secs;
      if (strong) c.strong += 1;
      row.cellMap.set(key, c);
    };

    videos.forEach((v) => {
      const acts = actsOf.get(v.filename);
      const env = envs[v.filename]?.environment || UNCLASSIFIED;
      if (dir === 'env') {
        const row = touch(env);
        row.videos += 1;
        row.seconds += v.duration;
        if (acts && acts.size) {
          row.tagged += 1;
          row.taggedSeconds += v.duration;
          acts.forEach((sim, a) => bump(row, a, v.duration, sim >= STRONG_SIM));
        }
      } else if (acts) {
        // One video contributes to every activity it matched, so the activity
        // rows overlap by construction — the same video is in several.
        acts.forEach((sim, a) => {
          const row = touch(a);
          row.videos += 1;
          row.seconds += v.duration;
          row.tagged += 1;
          row.taggedSeconds += v.duration;
          bump(row, env, v.duration, sim >= STRONG_SIM);
        });
      }
    });

    const baseOf = (key: string) =>
      dir === 'env'
        ? (actAll.get(key) || 0) / (taggedAll || 1)
        : (envAll.get(key) || 0) / (videos.length || 1);

    const rows: Group[] = Array.from(g.values()).map((row) => {
      const denom = dir === 'env' ? row.tagged : row.videos;
      const cells = Array.from(row.cellMap.values()).map((c) => {
        const share = c.videos / (denom || 1);
        const base = baseOf(c.key);
        return { ...c, share, base, lift: base > 0 ? share / base : 0 };
      });
      return { ...row, cells };
    });
    rows.sort((a, b) => b.videos - a.videos);
    return { groups: rows };
  }, [videos, envs, actsOf, dir]);

  // Keep a valid pick as the direction flips or the selection changes.
  useEffect(() => {
    if (groups.length === 0) return;
    if (!pick || !groups.some((r) => r.key === pick)) setPick(groups[0].key);
  }, [groups, pick]);
  useEffect(() => {
    setPick(null);
    setOpen(null);
  }, [dir]);

  const row = groups.find((r) => r.key === pick) || null;

  // One ordering, no control to explain: most distinctive first, and a row too
  // thin to trust (a 12x on 2 videos) sorts down with the ordinary ones rather
  // than leading the answer.
  const cells = useMemo(() => {
    if (!row) return [];
    const ok = (c: Cell) => c.videos >= MIN_N && c.strong >= MIN_STRONG;
    return [...row.cells].sort(
      (a, b) => (ok(b) ? b.lift : 0) - (ok(a) ? a.lift : 0) || b.videos - a.videos,
    );
  }, [row]);

  const rankable = (c: Cell) => c.videos >= MIN_N && c.strong >= MIN_STRONG;
  const ranked = useMemo(() => cells.filter(rankable), [cells]);
  const thinRows = useMemo(() => cells.filter((c) => !rankable(c)), [cells]);
  const shown = showThin ? [...ranked, ...thinRows] : ranked;

  const coverage = row && row.videos ? row.tagged / row.videos : 0;
  const thin = dir === 'env' && coverage < THIN_COVERAGE;

  // ---- evidence: the actual sentences behind one cell ----------------------
  const [samples, setSamples] = useState<{ key: string; rows: string[] } | null>(null);
  const [sampling, setSampling] = useState(false);
  const loadSamples = useCallback(async (cellKey: string) => {
    if (!row) return;
    const envKey = dir === 'env' ? row.key : cellKey;
    const actKey = dir === 'env' ? cellKey : row.key;
    const act = TOP_ACTIVITIES.find((a) => a.label === actKey);
    if (!act) return;
    setSampling(true);
    setSamples(null);
    try {
      // Non-slim, and only for the one activity someone asked to read.
      const per = await Promise.all(
        act.queries.map((q) => runDescriptionSearch(q, SEARCH_HIT_LIMIT).catch(() => [])),
      );
      const seen = new Set<string>();
      const out: string[] = [];
      per
        .flat()
        .sort((a, b) => b.similarity - a.similarity)
        .forEach((h) => {
          if (out.length >= 12 || seen.has(h.filename)) return;
          if ((envs[h.filename]?.environment || UNCLASSIFIED) !== envKey) return;
          seen.add(h.filename);
          if (h.description) out.push(h.description);
        });
      setSamples({ key: cellKey, rows: out });
    } finally {
      setSampling(false);
    }
  }, [row, dir, envs]);

  const onCell = (key: string) => {
    const next = open === key ? null : key;
    setOpen(next);
    if (next) loadSamples(next);
    else setSamples(null);
  };

  const cellNoun = dir === 'env' ? 'activity' : 'environment';

  return (
    <div className="ds-modal" onClick={onClose}>
      <div
        className="ds-checkout-box ds-stats-box ea-box"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ds-checkout-head">
          <b>Where &amp; what</b>
          {warming > 0 && <span className="ea-warm">tagging… {warming} left</span>}
          <div className="ea-toggles">
            <div className="ea-seg" role="group" aria-label="direction">
              <button
                type="button"
                className={dir === 'env' ? 'on' : ''}
                onClick={() => setDir('env')}
              >
                by environment
              </button>
              <button
                type="button"
                className={dir === 'act' ? 'on' : ''}
                onClick={() => setDir('act')}
              >
                by activity
              </button>
            </div>
          </div>
          <button onClick={onClose}>close</button>
        </div>

        <div className="ea-cols">
          <div className="ea-left">
            <div className="ea-list">
              {groups.map((r) => {
                const cov = r.videos ? r.tagged / r.videos : 0;
                return (
                  <button
                    type="button"
                    key={r.key}
                    className={`ea-grow${r.key === pick ? ' on' : ''}`}
                    onClick={() => {
                      setPick(r.key); setOpen(null); setSamples(null); setShowThin(false);
                    }}
                  >
                    <span className="ea-gname">{r.key}</span>
                    <span className="ea-gn">{r.videos.toLocaleString()}</span>
                    {dir === 'env' && (
                      <span
                        className={`ea-gcov${cov < THIN_COVERAGE ? ' thin' : ''}`}
                        title={`${r.tagged.toLocaleString()} carry an activity tag`}
                      >
                        {(cov * 100).toFixed(cov < 0.1 ? 1 : 0)}%
                      </span>
                    )}
                  </button>
                );
              })}
              {groups.length === 0 && <div className="ds-hint">loading…</div>}
            </div>
            {hiddenEnvs.length > 0 && dir === 'env' && (
              <div className="ea-hidden" title={hiddenEnvs.join(', ')}>
                +{hiddenEnvs.length} hidden by filters
              </div>
            )}
          </div>

          <div className="ea-right">
            {!row ? (
              <div className="ds-hint">pick one on the left</div>
            ) : (
              <>
                <div className="ea-title">
                  <span className="ea-tname">{row.key}</span>
                  <span className={`ea-cov${thin ? ' thin' : ''}`}>
                    <i style={{ width: `${Math.max(coverage * 100, 1.5)}%` }} />
                  </span>
                  <span className={`ea-covn${thin ? ' thin' : ''}`}>
                    {dir === 'env'
                      ? `${row.tagged.toLocaleString()} of ${row.videos.toLocaleString()} tagged`
                      : `${row.videos.toLocaleString()} videos · ${fmtHours(row.seconds / 3600)}`}
                  </span>
                  {cellNoun === 'activity' && (
                    <span className="ea-key">click a row to read the clips</span>
                  )}
                </div>

                <div className="ds-checkout-table ds-stats-matrix">
                  <table className="ds-table ea-table">
                    <thead>
                      <tr>
                        <th>{cellNoun === 'activity' ? 'Activity' : 'Environment'}</th>
                        <th className="num">Videos</th>
                        <th className="num">Hours</th>
                        <th className="ea-lifth">Compared to elsewhere</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((c) => {
                        const strong = c.lift >= LIFT_STRONG && rankable(c);
                        const weak = !rankable(c);
                        const isOpen = open === c.key;
                        return (
                          <React.Fragment key={c.key}>
                            <tr
                              className={`ea-row${strong ? ' strong' : ''}${isOpen ? ' open' : ''}`}
                              onClick={() => cellNoun === 'activity' && onCell(c.key)}
                            >
                              <td className="ds-q">{c.key}</td>
                              <td className="num">{c.videos.toLocaleString()}</td>
                              <td className="num">{fmtHours(c.seconds / 3600)}</td>
                              <td
                                className="ea-liftcell"
                                title={
                                  weak
                                    ? `${c.strong} of ${c.videos} are confident matches — needs ${MIN_STRONG}`
                                    : c.base > 0
                                      ? `${c.lift.toFixed(2)}x the rate across the whole selection`
                                      : undefined
                                }
                              >
                                {weak ? <span className="ea-nobar" /> : <LiftBar lift={c.lift} />}
                                <span className={`ea-liftn${strong ? ' strong' : ''}`}>
                                  {liftWords(c.lift, weak)}
                                </span>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="ea-samples">
                                <td colSpan={4}>
                                  {sampling && <div className="ds-hint">reading…</div>}
                                  {!sampling && samples?.key === c.key && (
                                    samples.rows.length ? (
                                      <ul>
                                        {samples.rows.map((s, i) => (
                                          <li key={i}>“{s}”</li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <div className="ds-hint">no description text</div>
                                    )
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {thinRows.length > 0 && (
                        <tr className="ea-morerow">
                          <td colSpan={4}>
                            <button type="button" onClick={() => setShowThin((v) => !v)}>
                              {showThin
                                ? `hide ${thinRows.length} more`
                                : `show ${thinRows.length} more`}
                            </button>
                          </td>
                        </tr>
                      )}
                      {shown.length === 0 && thinRows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="ds-hint">
                            {warming > 0 ? 'tagging…' : 'nothing tagged here'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
